import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getVenueLogs } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, ChevronLeft, ChevronRight } from "lucide-react";

const TYPE_LABELS: Record<string, string> = {
  "item:create": "Ürün Eklendi",
  "item:update": "Ürün Güncellendi",
  "item:delete": "Ürün Silindi",
  "item:bulk_price": "Toplu Fiyat",
  "menu:qr_scan": "QR Tarama",
  "order:new": "Yeni Sipariş",
};

const TYPE_COLORS: Record<string, string> = {
  "item:create": "default",
  "item:update": "secondary",
  "item:delete": "destructive",
  "item:bulk_price": "default",
};

const PAGE_SIZE = 20;

export function VenueLogsPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const { t } = useI18n();
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["venue-logs", venueId, page, typeFilter],
    queryFn: () => getVenueLogs(venueId!, { limit: PAGE_SIZE, offset: page * PAGE_SIZE, type: typeFilter || undefined }),
    enabled: !!venueId,
  });

  const types = ["item:create", "item:update", "item:delete", "item:bulk_price"];
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div className="max-w-2xl">
      <h1 className="mb-4 text-2xl font-bold flex items-center gap-2">
        <Activity className="size-6" />
        {t("activity_log")}
      </h1>

      <div className="mb-4 flex gap-2 flex-wrap">
        <Badge
          variant={!typeFilter ? "default" : "outline"}
          className="cursor-pointer"
          onClick={() => { setTypeFilter(""); setPage(0); }}
        >
          {t("all")}
        </Badge>
        {types.map((tp) => (
          <Badge
            key={tp}
            variant={typeFilter === tp ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => { setTypeFilter(tp); setPage(0); }}
          >
            {TYPE_LABELS[tp] || tp}
          </Badge>
        ))}
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">{t("loading")}</p>
      ) : !data?.logs.length ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Activity className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("no_logs")}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {data.logs.map((log) => (
              <Card key={log._id}>
                <CardContent className="py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={(TYPE_COLORS[log.type] as "default" | "secondary" | "destructive") || "secondary"} className="text-xs">
                        {TYPE_LABELS[log.type] || log.type}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.createdAt).toLocaleString("tr-TR")}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{log.message}</p>
                    {log.meta && Object.keys(log.meta).length > 0 && (
                      <div className="mt-1 flex gap-3 flex-wrap text-xs text-muted-foreground">
                        {log.meta.priceFrom != null && (
                          <span>{t("price")}: {String(log.meta.priceFrom)} → {String(log.meta.priceTo)}</span>
                        )}
                        {log.meta.oldPrice != null && (
                          <span>{t("old_price")}: {String(log.meta.oldPrice)}</span>
                        )}
                        {log.meta.percentage != null && (
                          <span>%{String(log.meta.percentage)} ({String(log.meta.itemCount)} {t("items_count")})</span>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-4">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-sm text-muted-foreground">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
